import type { ResultsResponse } from '@ekorank/shared'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { modelColor, modelLabel } from '@/lib/modelDisplay'
import type { ResultFilters } from '@/store/dashboard'
import { ModelIcon } from '@/components/ModelIcon'

interface Props {
  results: ResultsResponse | null
  loading: boolean
  sort: ResultFilters['sort']
  onSortChange: (col: ResultFilters['sort']['col']) => void
  onRowClick: (row: ResultsResponse['results'][number]) => void
}

const SORTABLE: { key: ResultFilters['sort']['col']; label: string }[] = [
  { key: 'date',      label: 'Date' },
  { key: 'model',     label: 'Model' },
  { key: 'mentioned', label: 'Mentioned' },
  { key: 'rank',      label: 'Rank' },
]

export function ResultsTable({ results, loading, sort, onSortChange, onRowClick }: Props) {
  if (loading && !results) {
    return <div className="p-12 flex justify-center"><LoadingSpinner /></div>
  }
  if (!results || results.results.length === 0) {
    return <div className="p-12 text-center text-on-surface-variant">No results match these filters.</div>
  }

  const sortIndicator = (col: string) => {
    if (sort.col !== col) return ''
    return sort.dir === 'asc' ? ' ↑' : ' ↓'
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-outline-variant/10">
            {SORTABLE.map((h) => (
              <th
                key={h.key}
                onClick={() => onSortChange(h.key)}
                className="text-left px-3 sm:px-6 py-2 sm:py-3 font-mono text-[10px] text-outline uppercase tracking-widest cursor-pointer hover:text-white"
              >
                {h.label}{sortIndicator(h.key)}
              </th>
            ))}
            <th className="text-left px-3 sm:px-6 py-2 sm:py-3 font-mono text-[10px] text-outline uppercase tracking-widest">Prompt</th>
            <th className="text-left px-3 sm:px-6 py-2 sm:py-3 font-mono text-[10px] text-outline uppercase tracking-widest">Details</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {results.results.map((r) => {
            const competitorNames = (r.competitors ?? []).map((c) => c.name)
            const fallbackCompetitors = !competitorNames.length && r.competitorsSnippet
              ? r.competitorsSnippet.split(' | ').map((s) => s.replace(/\*\*/g, '').trim()).filter(Boolean)
              : competitorNames
            return (
              <tr key={r.id} className="border-b border-outline-variant/5 hover:bg-surface-container-high/50 transition-colors">
                <td className="px-3 sm:px-6 py-3 sm:py-4 text-on-surface-variant">{new Date(r.checkedAt * 1000).toLocaleDateString()}</td>
                <td className="px-3 sm:px-6 py-3 sm:py-4">
                  <span className="px-2 py-1 rounded text-xs font-mono inline-flex items-center gap-1.5"
                        style={{ color: modelColor(r.model), background: `${modelColor(r.model)}15` }}>
                    <ModelIcon model={r.model} size={11} />
                    {modelLabel(r.model)}
                  </span>
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4">
                  <span className={`text-xs font-mono font-bold ${r.mentioned ? 'text-secondary' : 'text-error'}`}>
                    {r.mentioned ? 'YES' : 'NO'}
                  </span>
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 text-on-surface-variant">{r.rankPosition ?? '—'}</td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 text-white max-w-[280px] truncate">{r.promptText}</td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs max-w-[260px]">
                  {r.mentioned && r.contextSnippet ? (
                    <span className="text-on-surface-variant leading-relaxed">{r.contextSnippet}</span>
                  ) : !r.mentioned && fallbackCompetitors.length > 0 ? (
                    <div>
                      <p className="text-outline mb-1">Others mentioned instead:</p>
                      <ul className="space-y-0.5">
                        {fallbackCompetitors.slice(0, 3).map((c) => (
                          <li key={c} className="text-on-surface-variant capitalize">• {c}</li>
                        ))}
                        {fallbackCompetitors.length > 3 && (
                          <li className="text-outline">+{fallbackCompetitors.length - 3} more</li>
                        )}
                      </ul>
                    </div>
                  ) : (
                    <span className="text-outline">No details available</span>
                  )}
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4">
                  <button
                    onClick={() => onRowClick(r)}
                    className="text-xs text-outline hover:text-white border border-outline-variant/20 hover:border-outline-variant/50 rounded px-2 py-1 transition-colors whitespace-nowrap"
                  >
                    View response
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
